/**
 * @file ShorePayManageCard.tsx
 *
 * The connected-state view for Shore Pay, shown by {@link ShorePayConnectScreen}
 * once a Shore account exists. Two controls:
 *  - an Enable/Disable radio group that edits local state only; a Save button
 *    persists the change via `PUT /v1/payment-methods/shore/:id` (through
 *    {@link updatePaymentGateway}), toasts the outcome, and returns to the overview.
 *    Save is disabled until the selection differs from the persisted value.
 *  - a small "Complete Shore verification" text link that resumes the hosted KYC
 *    (a body-less `POST /v1/payment-methods/shore/connect`, which regenerates the
 *    link on the same account) and hands the browser off to it. Always shown.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ContentContainer, Icon, Radio } from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../../i18n/messages';
import { notifyError, notifySuccess } from '../../feedback/toast';
import { connectShorePay, updatePaymentGateway } from '../../api/payments';
import type { PaymentGateway } from '../../api/payments-types';
import { FormFooter } from '../../components/FormFooter';
import styles from '../payments.module.css';

interface ShorePayManageCardProps {
  /** The connected Shore gateway (carries the real `methodId` + current `enabled`). */
  gateway: PaymentGateway;
}

/**
 * Renders the Shore Pay management card (enable/disable + Save + KYC link).
 *
 * @param props - The connected Shore gateway.
 *
 * @returns The management screen.
 */
export function ShorePayManageCard({ gateway }: ShorePayManageCardProps) {
  const intl = useIntl();
  const navigate = useNavigate();
  const [enabled, setEnabled] = useState(gateway.enabled);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string>();
  const [kycLoading, setKycLoading] = useState(false);
  const [kycError, setKycError] = useState<string>();

  // Save is only meaningful once the selection diverges from the persisted value.
  const dirty = enabled !== gateway.enabled;

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    setSaveError(undefined);
    try {
      await updatePaymentGateway(gateway, { enabled });
      notifySuccess(intl.formatMessage(adminMessages.toastSaved));
      navigate('/payments');
    } catch {
      setSaveError(intl.formatMessage(adminMessages.paymentsShoreUpdateError));
      notifyError(intl.formatMessage(adminMessages.toastError));
      setSaving(false);
    }
  };

  const handleKyc = async (): Promise<void> => {
    setKycLoading(true);
    setKycError(undefined);
    try {
      // Body-less resume: the engine regenerates the KYC link on the existing account.
      const { actionUrl } = await connectShorePay();
      window.location.assign(actionUrl);
    } catch {
      setKycError(intl.formatMessage(adminMessages.paymentsShoreConnectError));
      notifyError(intl.formatMessage(adminMessages.toastError));
      setKycLoading(false);
    }
  };

  return (
    <div className={styles.form}>
      <header className="gv-flex gv-flex-col gv-w-full gv-gap-sm">
        <div className="gv-breadcrumbs">
          <Link to="/payments">
            <Icon name="arrow_back" size="small" />
            <FormattedMessage {...adminMessages.formBack} />
          </Link>
        </div>
        <h2 className="gv-heading-lg gv-m-0">
          <FormattedMessage {...adminMessages.paymentsShoreManageTitle} />
        </h2>
        <div className="gv-text-sm">
          <FormattedMessage {...adminMessages.paymentsShoreManageDescription} />
        </div>
      </header>

      <div className={`gv-flex gv-flex-col gv-gap-lg gv-w-full ${styles.column}`}>
        <ContentContainer className="gv-p-lg gv-flex gv-flex-col gv-gap-lg">
          <div className="gv-form-option gv-w-full">
            <span className="gv-label">
              <FormattedMessage {...adminMessages.paymentsStatusLabel} />
            </span>
            <div className="gv-flex gv-flex-col gv-gap-md">
              <label
                className="gv-flex gv-items-center gv-gap-sm"
                data-testid="shore-pay-status-active"
              >
                <Radio
                  groupName="shore-pay-status"
                  checked={enabled}
                  condensed
                  onChange={() => setEnabled(true)}
                />
                <span>
                  <FormattedMessage {...adminMessages.paymentsStatusActive} />
                </span>
              </label>
              <label
                className="gv-flex gv-items-center gv-gap-sm"
                data-testid="shore-pay-status-inactive"
              >
                <Radio
                  groupName="shore-pay-status"
                  checked={!enabled}
                  condensed
                  onChange={() => setEnabled(false)}
                />
                <span>
                  <FormattedMessage {...adminMessages.paymentsStatusInactive} />
                </span>
              </label>
            </div>
          </div>

          <div className="gv-form-option gv-w-full">
            <span className="gv-label">
              <FormattedMessage {...adminMessages.paymentsShoreKycLabel} />
            </span>
            <button
              type="button"
              className={`gv-text-sm ${styles.linkButton}`}
              data-testid="shore-pay-kyc-link"
              onClick={() => void handleKyc()}
              disabled={kycLoading}
            >
              {kycLoading ? (
                <FormattedMessage {...adminMessages.paymentsShoreConnecting} />
              ) : (
                <FormattedMessage {...adminMessages.paymentsShoreKycLink} />
              )}
            </button>
            {kycError ? (
              <span
                className="gv-text-sm gv-state-critical"
                role="alert"
                data-testid="shore-pay-kyc-error"
              >
                {kycError}
              </span>
            ) : null}
          </div>
        </ContentContainer>

        {saveError ? (
          <p
            className="gv-text-sm gv-state-critical"
            role="alert"
            data-testid="shore-pay-manage-error"
          >
            {saveError}
          </p>
        ) : null}

        <FormFooter>
          <Link
            to="/payments"
            className="gv-button gv-button-cancel"
            data-testid="shore-pay-manage-back"
          >
            <FormattedMessage {...adminMessages.formBack} />
          </Link>
          <button
            type="button"
            className="gv-button gv-button-primary"
            data-testid="shore-pay-manage-save"
            onClick={() => void handleSave()}
            disabled={saving || !dirty}
          >
            {saving ? (
              <FormattedMessage {...adminMessages.paymentsSaving} />
            ) : (
              <FormattedMessage {...adminMessages.paymentsSave} />
            )}
          </button>
        </FormFooter>
      </div>
    </div>
  );
}
